import React, { useEffect, useState } from 'react';
import Layout from '../../components/Layout';
import BankScopeField from '../../components/BankScopeField';
import ScopeField from '../../components/ScopeField';
import LotterySearchModal from '../../components/LotterySearchModal';
import LotteryCodeField from '../../components/LotteryCodeField';
import {
  createProductConfig, deleteProductConfig, listProductConfigs, listProducts, updateProductConfig,
} from '../../api/products';
import { listTirages } from '../../api/tirages';
import { resolveLotteryByCode } from '../../utils/lotteryLookup';
import './ParametresLoterieResultsView.css';

const diaOptionsFr = ['Todos', 'Lunes', 'Martes', 'Miercoles', 'Jueves', 'Viernes', 'Sabado', 'Domingo'];

const scopeParams = (filter, scopeId) => {
  if (filter === 'Groupe') return { group: scopeId };
  if (filter === 'Superviseur' || filter === 'Banque') return { bank: scopeId };
  return {};
};

// Niveau (ProductConfig.scope) correspondant au filtre de portee.
const scopeLevel = (filter) => {
  if (filter === 'Groupe') return 'group';
  if (filter === 'Superviseur' || filter === 'Banque') return 'bank';
  return 'company';
};

// "TOUS" (code 0) = parametre GENERAL de ce niveau (tirage vide cote backend,
// 2026-09-25) : il s'applique aux ventes de toutes les loteries SANS parametre
// propre — SANS rien inserer pour elles. Precision utilisateur (2026-09-26) :
// une loterie ouverte ici reste VIDE tant qu'on ne lui a pas volontairement
// cree son parametre ; on n'affiche donc que les parametres PROPRES a cette
// loterie (jamais les valeurs de TOUS pre-remplies), et Sauver n'insere QUE
// les produits remplis. Vider completement un produit = supprimer son
// parametre propre (la loterie retombe sur TOUS pour ce produit).

// Auto-complétion : L41/L51 sont les "meneurs". Une valeur saisie sur leur ligne
// se recopie sur les suivantes de la même famille (elles ont presque la même
// config). Éditer L42/L43/L52/L53 directement ne propage rien (ajustement fin).
const AUTOFILL_FOLLOWERS = {
  L41: ['L42', 'L43'],
  L51: ['L52', 'L53'],
};

// Commission de vente et Épargnes % sont INDEPENDANTS (demande utilisateur
// 2026-09-23 : plus de "= 100" automatique, ex. commission 20 / epargnes 0) et
// peuvent rester vides. Case vide -> 0 envoye au backend (DecimalField non null).
const OPTIONAL_PCT_FIELDS = [
  ['commissionVente', 'sales_commission'],
  ['epargnesPct', 'savings_pct'],
];
const isBlank = (v) => String(v ?? '').trim() === '';

const ParametresLoterieResultsView = ({
  lottery, lotteryName, jour, filter, scopeId, setLottery, setLotteryName, onBack,
}) => {
  const [rows, setRows] = useState([]);
  const [showSwitchLotteryModal, setShowSwitchLotteryModal] = useState(false);
  const [codeInput, setCodeInput] = useState(String(lottery));
  const [lookupError, setLookupError] = useState('');
  const [loading, setLoading] = useState(true);
  const [hasExisting, setHasExisting] = useState(false);
  const [ticketMin, setTicketMin] = useState('');
  const [commentaire, setCommentaire] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [saved, setSaved] = useState(false);
  const [reloadTick, setReloadTick] = useState(0);
  const [confirmReset, setConfirmReset] = useState(false);
  const [resetting, setResetting] = useState(false);

  const [multiplesLoterias, setMultiplesLoterias] = useState(false);
  // Cible par defaut : la loterie affichee, sauf depuis TOUS (copier TOUS sur
  // lui-meme n'a pas de sens -> cible vide, a choisir).
  const [copyLottery, setCopyLottery] = useState(String(lottery) === '0' ? '' : lottery);
  const [copyLotteryName, setCopyLotteryName] = useState(String(lottery) === '0' ? '' : lotteryName);
  // Mode "Multiples Loterias" : liste deroulante de toutes les loteries avec cases a
  // cocher — on copie la config vers chaque loterie cochee (au lieu d'une seule).
  const [allLotteries, setAllLotteries] = useState([]);
  const [lotteriesLoading, setLotteriesLoading] = useState(false);
  const [lotterySearch, setLotterySearch] = useState('');
  const [selectedLotteryCodes, setSelectedLotteryCodes] = useState([]);
  // Portee cible par defaut = celle qu'on consulte (avant : toujours 'Banque',
  // sans banque choisie -> "Coche au moins une loterie et choisis une cible"
  // meme avec des loteries cochees, 2026-09-26).
  const [copyFilter, setCopyFilter] = useState(filter || 'Tout');
  const [copyBank, setCopyBank] = useState('');
  const [copyBankName, setCopyBankName] = useState('');
  const [copyDia, setCopyDia] = useState('Todos');
  const [copying, setCopying] = useState(false);
  const [copyError, setCopyError] = useState('');
  const [copySaved, setCopySaved] = useState(false);

  const isAllLotteries = String(lottery) === '0';

  // Garde le champ code synchro quand la loterie change autrement (modal 🔍).
  useEffect(() => { setCodeInput(String(lottery)); setLookupError(''); }, [lottery]);

  // On quitte le champ : on résout le code tapé -> nom, et on bascule dessus.
  const handleLotteryBlur = async () => {
    const c = codeInput.trim();
    if (c === '' || c === String(lottery)) { setLookupError(''); return; }
    const res = await resolveLotteryByCode(c);
    if (res) {
      setLookupError('');
      setLottery(res.code);
      setLotteryName(res.name);
    } else {
      setLookupError('Loterie introuvable');
    }
  };

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError('');
    setSaved(false);
    const scope = scopeParams(filter, scopeId);
    const level = scopeLevel(filter);
    // Seulement les parametres PROPRES a ce niveau + cette loterie (ou TOUS).
    const configsPromise = listProductConfigs({ tirage: isAllLotteries ? '0' : lottery, ...scope });

    Promise.all([listProducts(), configsPromise])
      .then(([products, configs]) => {
        if (cancelled) return;
        const configByProduct = Object.fromEntries(configs.map((c) => [c.product, c]));
        const wantedTirage = isAllLotteries ? 0 : Number(lottery);
        const nextRows = products.map((p) => {
          const cfg = configByProduct[p.id];
          // Pas de parametre propre -> cases VIDES (rien n'est insere tant qu'on
          // ne remplit pas volontairement ce produit).
          const own = Boolean(cfg) && cfg.scope === level && Number(cfg.tirage) === wantedTirage
            ? cfg : null;
          return {
            productId: p.id,
            name: p.name,
            abbr: p.abbr,
            configId: own ? own.id : null,
            prixPoints: own ? own.points_price : '',
            limiteEnLigne: own ? own.online_limit : '',
            limiteLocal: own ? own.local_limit : '',
            commissionVente: own ? own.sales_commission : '',
            epargnesPct: own ? own.savings_pct : '',
            keys: p.payout_keys.map((k) => {
              const payout = own?.payouts.find((pay) => pay.key === k.id);
              return { keyId: k.id, key: k.key, value: payout ? payout.won_per_point : '' };
            }),
          };
        });
        setRows(nextRows);
        setHasExisting(nextRows.some((r) => r.configId));
      })
      .catch(() => { if (!cancelled) setError('Impossible de charger les produits/configurations.'); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [lottery, filter, scopeId, isAllLotteries, reloadTick]);

  useEffect(() => {
    if (!multiplesLoterias || allLotteries.length > 0) return;
    let cancelled = false;
    setLotteriesLoading(true);
    listTirages()
      .then((data) => { if (!cancelled) setAllLotteries(data); })
      .catch(() => { if (!cancelled) setCopyError('Impossible de charger la liste des loteries.'); })
      .finally(() => { if (!cancelled) setLotteriesLoading(false); });
    return () => { cancelled = true; };
  }, [multiplesLoterias, allLotteries.length]);

  const toggleLotteryCode = (code) => {
    const c = String(code);
    setSelectedLotteryCodes((prev) => (
      prev.includes(c) ? prev.filter((x) => x !== c) : [...prev, c]
    ));
    setCopySaved(false);
  };

  const filteredLotteries = allLotteries.filter((l) =>
    l.name.toLowerCase().includes(lotterySearch.toLowerCase())
    || String(l.code).includes(lotterySearch.trim())
  );

  // Code loterie "0" = le parametre TOUS lui-meme (UNE ligne par produit, tirage
  // vide cote backend) — jamais de recopie automatique dans les autres loteries.
  const resolveLotteryCodes = async (code) => [String(code)];

  const updateRowField = (rowIndex, field, value) => {
    setRows((prev) => {
      const followers = AUTOFILL_FOLLOWERS[prev[rowIndex].abbr] || [];
      return prev.map((r, i) => {
        if (i === rowIndex || followers.includes(r.abbr)) return { ...r, [field]: value };
        return r;
      });
    });
    setSaved(false);
  };

  const updateKeyValue = (rowIndex, keyIndex, value) => {
    setRows((prev) => {
      const edited = prev[rowIndex];
      const followers = AUTOFILL_FOLLOWERS[edited.abbr] || [];
      return prev.map((r, i) => {
        if (i === rowIndex) {
          return { ...r, keys: r.keys.map((k, ki) => (ki === keyIndex ? { ...k, value } : k)) };
        }
        if (followers.includes(r.abbr)) {
          // Recopie PAR POSITION (pas par nom de cle) : L41/L42/L43 ont chacun
          // une seule cle mais de nom different (1st / 2nd / 3rd), idem L51/L52/L53
          // — la comparaison par nom ne recopiait donc jamais "Gagne par point".
          return { ...r, keys: r.keys.map((k, ki) => (ki === keyIndex ? { ...k, value } : k)) };
        }
        return r;
      });
    });
    setSaved(false);
  };

  // Produit entierement vide (aucune case remplie) = pas de parametre propre.
  const isRowEmpty = (row) => (
    [row.prixPoints, row.limiteEnLigne, row.limiteLocal, row.commissionVente, row.epargnesPct].every(isBlank)
    && row.keys.every((k) => isBlank(k.value))
  );

  const buildPayload = (row, lotteryCode, targetScope) => {
    const zeroIfBlank = (v) => (isBlank(v) ? '0' : v);
    const payload = {
      tirage: Number(lotteryCode),
      product: row.productId,
      group: targetScope.group || null,
      bank: targetScope.bank || null,
      points_price: zeroIfBlank(row.prixPoints),
      online_limit: zeroIfBlank(row.limiteEnLigne),
      local_limit: zeroIfBlank(row.limiteLocal),
      payouts: row.keys.filter((k) => !isBlank(k.value)).map((k) => ({ key: k.keyId, won_per_point: k.value })),
    };
    OPTIONAL_PCT_FIELDS.forEach(([rowField, apiField]) => { payload[apiField] = zeroIfBlank(row[rowField]); });
    return payload;
  };

  // Copie / sauvegarde vers une loterie (ou TOUS) : seuls les produits REMPLIS
  // sont inseres/mis a jour ; un produit vide n'est jamais insere.
  const copyConfigToLottery = async (lotteryCode, targetScope) => {
    const existingTargetConfigs = await listProductConfigs({ tirage: lotteryCode, ...targetScope });
    const existingByProduct = Object.fromEntries(existingTargetConfigs.map((c) => [c.product, c]));

    await Promise.all(rows.filter((row) => !isRowEmpty(row)).map((row) => {
      const payload = buildPayload(row, lotteryCode, targetScope);
      const existing = existingByProduct[row.productId];
      return existing ? updateProductConfig(existing.id, payload) : createProductConfig(payload);
    }));
  };

  const handleSave = async (e) => {
    e.preventDefault();
    setSaving(true);
    setError('');
    setSaved(false);
    try {
      const scope = scopeParams(filter, scopeId);
      // Produits remplis -> inseres / mis a jour ; produit VIDE qui avait un
      // parametre propre -> supprime (retombe sur TOUS) ; vide sans parametre -> rien.
      await copyConfigToLottery(String(lottery), scope);
      for (const row of rows) {
        if (row.configId && isRowEmpty(row)) await deleteProductConfig(row.configId);
      }
      setReloadTick((n) => n + 1); // recharge : configId a jour
      setSaved(true);
    } catch (err) {
      setError('Sauvegarde impossible — verifie les valeurs saisies.');
    } finally {
      setSaving(false);
    }
  };

  // "Revenir au parametre general" : supprime les parametres PROPRES a cette
  // loterie pour ce niveau -> elle reprend TOUS (ou le niveau au-dessus).
  const handleResetToGeneral = async () => {
    setResetting(true);
    setError('');
    setSaved(false);
    try {
      for (const r of rows) {
        if (r.configId) await deleteProductConfig(r.configId);
      }
      setConfirmReset(false);
      setReloadTick((n) => n + 1);
    } catch (err) {
      setError('Impossible de revenir au parametre general.');
    } finally {
      setResetting(false);
    }
  };


  // Copie (depuis TOUS ou une loterie) : seuls les produits REMPLIS sont copies,
  // les vides sont ignores (la cible suit TOUS pour eux) — 2026-09-26, "je dois
  // pouvoir copier TOUS vers une ou plusieurs loteries" : l'ancienne regle
  // "TOUS les produits remplis" bloquait des qu'un seul produit etait vide.
  // Garde-fou conserve (demande 2026-09-24) : un produit COPIE doit avoir Prix
  // Points et Limite en ligne > 0 (MGRA exempte : offert, montant 0).
  const filledRows = rows.filter((r) => !isRowEmpty(r));
  const incompleteProducts = filledRows
    .filter((r) => String(r.abbr).toUpperCase() !== 'MGRA')
    .filter((r) => !(Number(r.prixPoints) > 0) || !(Number(r.limiteEnLigne) > 0))
    .map((r) => r.abbr || r.name);
  const nothingToCopy = filledRows.length === 0;
  // Rien d'ENREGISTRE a ce niveau pour cette loterie (ou TOUS) -> pas de panneau
  // Copier (2026-09-27, "TOUS est vide et malgre tout la section copiar est
  // apparue"). Il revient apres Sauver (rechargement -> configId renseigne).
  const hasSavedConfig = rows.some((r) => r.configId);

  // Cible de la copie : par defaut la MEME portee que la source (on choisit
  // seulement la/les loterie(s), 2026-09-26 "je ne peux pas copier seulement
  // pour la loterie ?"). Choisir une autre banque/un autre groupe = copier ailleurs.
  const copyToSameScope = copyFilter === filter && !copyBank;
  const copyTargetScope = copyToSameScope ? scopeParams(filter, scopeId) : scopeParams(copyFilter, copyBank);
  const copyTargetLabel = copyToSameScope
    ? `meme portee que la source (${filter}${filter !== 'Tout' ? ` #${scopeId}` : ''})`
    : `${copyFilter}${copyFilter !== 'Tout' ? ` ${copyBankName || `#${copyBank}`}` : ' (company)'}`;

  const handleCopiar = async (e) => {
    e.preventDefault();
    if (nothingToCopy) {
      setCopyError('Rien a copier : aucun produit rempli.');
      return;
    }
    if (incompleteProducts.length > 0) {
      setCopyError(`Copie impossible : Prix Points et Limite en ligne requis pour ${incompleteProducts.join(', ')}.`);
      return;
    }
    // Copier TOUS sur le TOUS de la meme portee = copier sur lui-meme.
    const copyTargets = multiplesLoterias ? selectedLotteryCodes : [String(copyLottery)];
    const sameScope = JSON.stringify(copyTargetScope) === JSON.stringify(scopeParams(filter, scopeId));
    if (isAllLotteries && sameScope && copyTargets.includes('0')) {
      setCopyError('Choisis une loterie cible (TOUS ne peut pas etre copie sur lui-meme).');
      return;
    }
    if (multiplesLoterias ? selectedLotteryCodes.length === 0 : !copyLottery) {
      setCopyError(multiplesLoterias ? 'Coche au moins une loterie.' : 'Choisis une loterie cible.');
      return;
    }
    if (!copyToSameScope && copyFilter !== 'Tout' && !copyBank) {
      setCopyError(`Choisis ${copyFilter === 'Groupe' ? 'le groupe' : 'la banque'} cible (ou coche « Tout » pour la company).`);
      return;
    }
    setCopying(true);
    setCopyError('');
    setCopySaved(false);
    try {
      const targetCodes = multiplesLoterias
        ? selectedLotteryCodes
        : await resolveLotteryCodes(copyLottery);
      const targetScope = copyTargetScope;
      // En serie (pas Promise.all) : chaque loterie fait un GET puis des POST/PATCH,
      // et on veut un message d'erreur clair sur la premiere qui echoue.
      for (const code of targetCodes) {
        await copyConfigToLottery(code, targetScope);
      }
      setCopySaved(true);
    } catch (err) {
      setCopyError("Copie impossible — verifie que la cible appartient bien a ta company.");
    } finally {
      setCopying(false);
    }
  };

  return (
    <Layout>
      <p className="plr-back" onClick={onBack}>◀ Back to Search</p>

      <div className="plr-columns">
        <div className="plr-left">
          <div className="plr-card">
            <div className="plr-card-header">
              <span className="plr-card-title">Loterie</span>
            </div>
            <div className="plr-card-body">
              <div className="plr-info-row">
                <div>
                  <div className="plr-line plr-lottery-switch">
                    <span>Loterie:</span>
                    <input
                      className="plr-input plr-input-sm"
                      type="text"
                      inputMode="numeric"
                      value={codeInput}
                      onChange={(e) => setCodeInput(e.target.value)}
                      onFocus={(e) => e.target.select()}
                      onBlur={handleLotteryBlur}
                      title="Tape le code de la loterie puis sors du champ"
                    />
                    {setLottery && (
                      <span
                        className="plr-icon-btn"
                        title="Chercher une loterie"
                        onClick={() => setShowSwitchLotteryModal(true)}
                      >
                        🔍
                      </span>
                    )}
                    <strong className={lookupError ? 'plr-lookup-err' : ''}>
                      {lookupError || lotteryName}
                    </strong>
                  </div>
                  <p className="plr-line">Jour: {jour}</p>
                  <p className="plr-line">Portee: {filter}{filter !== 'Tout' ? ` #${scopeId}` : ''}</p>
                </div>
                {hasExisting && <p className="plr-existing-notice">Registre existant a modifier</p>}
              </div>

              {showSwitchLotteryModal && (
                <LotterySearchModal
                  onSelect={(id, name) => {
                    setLottery(String(id));
                    setLotteryName(name || '');
                  }}
                  onClose={() => setShowSwitchLotteryModal(false)}
                />
              )}

              {isAllLotteries ? (
                <p className="plr-all-hint">
                  Mode TOUS : parametre GENERAL de cette portee. Il s'applique a toutes les
                  loteries qui n'ont pas leur propre parametre. Les loteries reglees
                  explicitement (ex. FLORIDA) ne sont pas modifiees.
                </p>
              ) : (
                !loading && (
                  <div className="plr-all-hint">
                    {hasExisting
                      ? 'Les produits remplis sont les parametres PROPRES a cette loterie : modifier TOUS ne les change pas. '
                      : 'Cette loterie n\'a pas de parametre propre : c\'est le parametre general (TOUS) qui s\'applique. '}
                    Cases vides = TOUS s'applique pour ce produit. Remplis seulement ce qui doit etre
                    different, puis Sauver (vider un produit supprime son parametre propre).
                    {hasExisting && (
                      <div className="plr-reset-row">
                        {!confirmReset ? (
                          <button type="button" className="plr-reset-btn" onClick={() => setConfirmReset(true)}>
                            Revenir au parametre general (TOUS)
                          </button>
                        ) : (
                          <>
                            <span>Supprimer le parametre propre a cette loterie ?</span>
                            <button type="button" className="plr-reset-btn plr-reset-confirm" disabled={resetting} onClick={handleResetToGeneral}>
                              {resetting ? '...' : 'Oui, revenir a TOUS'}
                            </button>
                            <button type="button" className="plr-reset-btn" disabled={resetting} onClick={() => setConfirmReset(false)}>
                              Annuler
                            </button>
                          </>
                        )}
                      </div>
                    )}
                  </div>
                )
              )}

              <div className="plr-row">
                <label className="plr-label">Ticket Valeur minimale</label>
                <input
                  className="plr-input"
                  type="text"
                  value={ticketMin}
                  onChange={e => setTicketMin(e.target.value)}
                />
              </div>

              <div className="plr-row plr-row-top">
                <label className="plr-label">Commentaire</label>
                <textarea
                  className="plr-textarea"
                  value={commentaire}
                  onChange={e => setCommentaire(e.target.value)}
                />
              </div>

              {loading ? (
                <p>Chargement...</p>
              ) : (
                <form onSubmit={handleSave}>
                  <div className="plr-details-bar">Details</div>

                  <p className="plr-help">
                    Aide : les valeurs saisies sur <strong>L41</strong> se recopient
                    automatiquement sur <strong>L42</strong> et <strong>L43</strong> (idem
                    <strong> L51</strong> → <strong>L52</strong>/<strong>L53</strong>), car
                    ces loteries ont presque la même config. Modifiez ensuite L42/L43
                    (ou L52/L53) directement si une valeur diffère.
                    <br />
                    <strong>Commission de vente</strong> et <strong>Épargnes %</strong> sont
                    indépendants (ex. commission 20, épargnes 0) et peuvent rester vides
                    (case vide = 0).
                  </p>

                  {rows.map((row, gi) => (
                    <div key={row.productId} className="plr-group">
                      <p className="plr-group-name">
                        {row.name}
                        {!isAllLotteries && !row.configId && (
                          <span className="plr-inherited" title="Aucun parametre propre : le parametre general s'applique">
                            TOUS s'applique
                          </span>
                        )}
                      </p>
                      <table className="plr-table">
                        <thead>
                          <tr>
                            <th>Produit</th>
                            <th>Prix Points</th>
                            <th>Limite en ligne</th>
                            <th>Limite Local</th>
                            <th>Commission de vente</th>
                            <th>Epargnes %</th>
                          </tr>
                        </thead>
                        <tbody>
                          <tr>
                            <td>{row.name}</td>
                            <td><input className="plr-cell-input" value={row.prixPoints} onChange={e => updateRowField(gi, 'prixPoints', e.target.value)} /></td>
                            <td><input className="plr-cell-input" value={row.limiteEnLigne} onChange={e => updateRowField(gi, 'limiteEnLigne', e.target.value)} /></td>
                            <td><input className="plr-cell-input" value={row.limiteLocal} onChange={e => updateRowField(gi, 'limiteLocal', e.target.value)} /></td>
                            <td><input className="plr-cell-input" value={row.commissionVente} onChange={e => updateRowField(gi, 'commissionVente', e.target.value)} /></td>
                            <td><input className="plr-cell-input" value={row.epargnesPct} onChange={e => updateRowField(gi, 'epargnesPct', e.target.value)} /></td>
                          </tr>
                        </tbody>
                      </table>

                      <table className="plr-key-table">
                        <thead>
                          <tr>
                            <th>Kle</th>
                            <th>Gagne par point</th>
                          </tr>
                        </thead>
                        <tbody>
                          {row.keys.map((k, ki) => (
                            <tr key={k.keyId}>
                              <td>{k.key}</td>
                              <td><input className="plr-cell-input" value={k.value} onChange={e => updateKeyValue(gi, ki, e.target.value)} /></td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  ))}

                  {error && <p className="plr-error">{error}</p>}
                  {saved && <p className="plr-saved">Sauvegarde.</p>}

                  <button type="submit" className="plr-save-btn" disabled={saving}>
                    {saving ? 'Sauvegarde...' : 'Sauver'}
                  </button>
                </form>
              )}
            </div>
          </div>
        </div>

        {/* Copier Parametres : disponible aussi depuis TOUS (2026-09-25 — TOUS est
            desormais un vrai parametre general, qu'on peut copier vers une ou
            plusieurs loteries pour leur donner un parametre propre). Toujours A COTE
            de la liste, meme sur telephone (colonne etroite collante, cf. CSS). */}
        {hasSavedConfig && (
        <div className="plr-right">
          <div className="plr-card">
            <div className="plr-card-header">
              <span className="plr-card-title">Loterie</span>
            </div>
            <div className="plr-card-body">
              <div className="plr-copy-panel">
                <div className="plr-copy-header">Copier Parametres</div>

                <p className="plr-copy-section-title">From</p>
                <p className="plr-line">Loterie : {lotteryName}</p>
                <p className="plr-line">Dias: {jour}</p>

                <p className="plr-copy-section-title">Copier</p>

                <form onSubmit={handleCopiar}>
                  <div className="plr-copy-row">
                    <label className="plr-copy-checkbox-label">
                      <input
                        type="checkbox"
                        checked={multiplesLoterias}
                        onChange={e => { setMultiplesLoterias(e.target.checked); setCopySaved(false); }}
                      />
                      Multiples Loterias
                    </label>
                  </div>

                  {!multiplesLoterias ? (
                    <>
                      <div className="plr-copy-row">
                        <label className="plr-copy-label">Loteri</label>
                        <LotteryCodeField
                          value={copyLottery}
                          name={copyLotteryName}
                          onChange={(code) => { setCopyLottery(code); setCopyLotteryName(''); }}
                          onResolve={(code, nm) => { setCopyLottery(code); setCopyLotteryName(nm); }}
                          inputClass="plr-copy-input plr-copy-input-sm"
                          iconClass="plr-icon-btn"
                          nameClass="plr-copy-name"
                        />
                      </div>
                    </>
                  ) : (
                    <div className="plr-lottery-multi">
                      <input
                        className="plr-copy-input plr-lottery-multi-search"
                        type="text"
                        placeholder="Filtrer les loteries..."
                        value={lotterySearch}
                        onChange={e => setLotterySearch(e.target.value)}
                      />
                      <div className="plr-lottery-multi-actions">
                        <span
                          className="plr-lottery-multi-link"
                          onClick={() => { setSelectedLotteryCodes(filteredLotteries.map(l => String(l.code))); setCopySaved(false); }}
                        >
                          Tout cocher
                        </span>
                        <span
                          className="plr-lottery-multi-link"
                          onClick={() => { setSelectedLotteryCodes([]); setCopySaved(false); }}
                        >
                          Tout decocher
                        </span>
                        <span className="plr-lottery-multi-count">{selectedLotteryCodes.length} selectionnee(s)</span>
                      </div>
                      <div className="plr-lottery-multi-list">
                        {lotteriesLoading && <p className="plr-line">Chargement...</p>}
                        {!lotteriesLoading && filteredLotteries.length === 0 && (
                          <p className="plr-line">Aucune loterie.</p>
                        )}
                        {filteredLotteries.map((l) => (
                          <label key={l.id} className="plr-lottery-multi-item">
                            <input
                              type="checkbox"
                              checked={selectedLotteryCodes.includes(String(l.code))}
                              onChange={() => toggleLotteryCode(l.code)}
                            />
                            <span className="plr-lottery-multi-code">{l.code}</span>
                            <span>{l.name}</span>
                          </label>
                        ))}
                      </div>
                    </div>
                  )}

                  <div className="plr-copy-radio-row">
                    {['Tout', 'Groupe', 'Superviseur', 'Banque'].map((opt) => (
                      <label key={opt} className="plr-copy-radio-label">
                        <input
                          type="radio"
                          name="copyFilter"
                          value={opt}
                          checked={copyFilter === opt}
                          onChange={() => { setCopyFilter(opt); setCopyBank(''); setCopyBankName(''); }}
                        />
                        {opt}
                      </label>
                    ))}
                  </div>
                  <p className="plr-line">Cible : {copyTargetLabel}</p>

                  {copyFilter !== 'Tout' && (
                    <div className="plr-copy-row">
                      <label className="plr-copy-label">{copyFilter}</label>
                      {(copyFilter === 'Banque' || copyFilter === 'Superviseur') ? (
                        <BankScopeField
                          id={copyBank}
                          name={copyBankName}
                          onResolve={(id, name) => { setCopyBank(id); setCopyBankName(name); }}
                          inputClass="plr-copy-input plr-copy-input-sm"
                          iconClass="plr-icon-btn"
                          nameClass="plr-copy-name"
                          errClass="plr-lookup-err"
                        />
                      ) : (
                        <ScopeField
                          filter={copyFilter}
                          id={copyBank}
                          name={copyBankName}
                          onResolve={(id, nm) => { setCopyBank(id); setCopyBankName(nm); }}
                          inputClass="plr-copy-input plr-copy-input-sm"
                          iconClass="plr-icon-btn"
                          nameClass="plr-copy-name"
                          errClass="plr-lookup-err"
                        />
                      )}
                    </div>
                  )}

                  <div className="plr-copy-row">
                    <label className="plr-copy-label">Dia:</label>
                    <select className="plr-copy-select" value={copyDia} onChange={e => setCopyDia(e.target.value)}>
                      {diaOptionsFr.map(opt => <option key={opt}>{opt}</option>)}
                    </select>
                  </div>

                  {!loading && (nothingToCopy ? (
                    <p className="plr-error">Rien a copier : aucun produit rempli.</p>
                  ) : incompleteProducts.length > 0 && (
                    <p className="plr-error">
                      Un produit copie doit avoir Prix Points et Limite en ligne. A completer
                      (ou vider) : {incompleteProducts.join(', ')}.
                    </p>
                  ))}
                  {!loading && !nothingToCopy && (
                    <p className="plr-line">
                      {filledRows.length} produit(s) rempli(s) seront copies ; les produits
                      vides sont ignores.
                    </p>
                  )}
                  {copyError && <p className="plr-error">{copyError}</p>}
                  {copySaved && <p className="plr-saved">Copie.</p>}

                  <button
                    type="submit"
                    className="plr-copiar-btn"
                    disabled={copying || loading || nothingToCopy || incompleteProducts.length > 0}
                  >
                    {copying ? 'Copie...' : 'Copiar'}
                  </button>
                </form>
              </div>
            </div>
          </div>
        </div>
        )}
      </div>
    </Layout>
  );
};

export default ParametresLoterieResultsView;
